import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { environment } from '../../../environment/environment';
import {
  ApiResponse,
  ApiResponseWithoutData,
} from '../models/api-response.model';
import { LoginUserModel, UserResponseModel } from '../models/user.model';
import { isNullOrUndefined } from '../../shared/utils/checker.utils';
import { parseUser } from '../../shared/utils/parser.utils';
import { User } from './user';

/**
 * Authenticates users, validates and ends sessions, and maintains the shared authentication state.
 */
@Injectable({
  providedIn: 'root',
})
export class Auth {
  /** Sends authentication and session requests to the backend. */
  private readonly http = inject(HttpClient);
  /** Stores the parsed user returned by authentication and session checks. */
  private readonly userService = inject(User);
  /** Redirects users when a session is invalid or has ended. */
  private readonly router = inject(Router);
  /** Base URL for authentication and session endpoints. */
  private readonly serviceUrl = environment.authority_short + 'dashboard/auth/';

  /** Indicates whether the latest session check or authentication operation succeeded. */
  public authCheck = false;

  /**
   * Builds the FormData payload for a login request.
   * @param user Login model containing email and password.
   * @returns FormData object matching backend login field names.
   */
  private buildLoginPayload(user: LoginUserModel): FormData {
    const formData = new FormData();
    formData.append('email', user.email);
    formData.append('password', user.password);

    return formData;
  }

  /**
   * Handles successful authentication responses by updating local auth state and user state.
   * @param response API response containing user data.
   * @returns Raw user response object from the backend.
   */
  private setAuthenticatedUser(
    response: ApiResponse<UserResponseModel>
  ): UserResponseModel {
    this.authCheck = true;
    this.userService.setUser(parseUser(response.data));
    return response.data;
  }

  /**
   * Authenticates a user and stores the returned user in User.
   * @param user Login model containing email and password.
   * @returns User data from the backend for the authenticated session.
   */
  public async login(user: LoginUserModel): Promise<UserResponseModel> {
    const payload = this.buildLoginPayload(user);

    const response = await firstValueFrom(
      this.http
        .post<ApiResponse<UserResponseModel>>(
          this.serviceUrl + 'login.php',
          payload
        )
        .pipe(
          catchError(error => {
            this.authCheck = false;
            if (error.status === 401) {
              return throwError(() => new Error('Invalid credentials'));
            }

            return throwError(() => new Error('Login failed'));
          })
        )
    );

    console.debug('login(): ', response);
    return this.setAuthenticatedUser(response);
  }

  /**
   * Verifies whether the current user session is still valid.
   * @returns User data from the backend when the session is valid.
   */
  public async checkSession(): Promise<UserResponseModel> {
    const response = await firstValueFrom(
      this.http
        .post<ApiResponse<UserResponseModel>>(this.serviceUrl + 'check.php', {})
        .pipe(
          catchError(error => {
            this.authCheck = false;
            this.router.navigate(['/login']);

            if (error.status === 401) {
              return throwError(
                () => new Error('Session expired, redirecting to login')
              );
            }

            return throwError(() => new Error('Login failed'));
          })
        )
    );

    this.userService.setUser(parseUser(response.data));

    console.debug('checkSession(): ', response);
    return this.setAuthenticatedUser(response);
  }

  /**
   * Logs the user out, clears local user state, and redirects to the login page.
   * @returns Backend logout response containing success metadata.
   */
  public async logout(): Promise<ApiResponseWithoutData> {
    const response = await firstValueFrom(
      this.http
        .post<ApiResponseWithoutData>(this.serviceUrl + 'logout.php', {})
        .pipe(
          catchError(error => {
            this.authCheck = false;
            if (error.status === 401) {
              return throwError(() => new Error('Invalid credentials'));
            }

            return throwError(() => new Error('Logout failed'));
          })
        )
    );

    console.debug('logout(): ', response);
    this.authCheck = false;
    this.userService.setUser(null);
    this.router.navigate(['/login']);

    return response;
  }

  /**
   * Checks whether a user is currently logged in by validating the active session.
   * @returns True when a valid user session exists; otherwise false.
   */
  public async isLoggedIn(): Promise<boolean> {
    try {
      const user = await this.checkSession();
      return !isNullOrUndefined(user);
    } catch (error) {
      console.error('Error checking user session:', error);
      return false;
    }
  }
}
